(function () {
  "use strict";

  const CONTROLLER_KEY = "__gibbonMobileMenuController";
  const HEADER_ROOT_SELECTOR = "#header-sticky";
  const DESKTOP_QUERY = "(min-width: 769px)";
  let editorMobileBlockId = null;

  const previousController = window[CONTROLLER_KEY];
  if (previousController && typeof previousController.destroy === "function") {
    previousController.destroy();
  }

  function findHeaderRoot(scope) {
    if (
      !scope ||
      (scope.nodeType !== Node.ELEMENT_NODE &&
        scope.nodeType !== Node.DOCUMENT_NODE)
    ) {
      return null;
    }

    if (scope.matches && scope.matches(HEADER_ROOT_SELECTOR)) {
      return scope;
    }

    return scope.querySelector
      ? scope.querySelector(HEADER_ROOT_SELECTOR)
      : null;
  }

  function findElementById(scope, id) {
    if (!scope || !id) return null;

    if (scope.id === id) return scope;

    return (
      Array.from(scope.querySelectorAll("[id]")).find(
        (element) => element.id === id,
      ) || null
    );
  }

  function directChild(element, selector) {
    if (!element) return null;

    return (
      Array.from(element.children).find((child) => child.matches(selector)) ||
      null
    );
  }

  function directSubmenu(listItem) {
    return directChild(listItem, "ul");
  }

  function createController(headerRoot) {
    const headerShell =
      headerRoot.closest(".shopify-section-group-header-group") ||
      headerRoot.closest("[id^='shopify-section-']") ||
      headerRoot.parentElement;

    if (!headerShell) return null;

    const menuOpenButton = headerRoot.querySelector(
      ".menu_open[data-mobile-nav-toggle], .menu_open",
    );
    const controlledPanelId = menuOpenButton?.getAttribute("aria-controls");
    const menuPanel =
      findElementById(headerShell, controlledPanelId) ||
      findElementById(headerShell, "MobileMenu") ||
      headerShell.querySelector("[data-mobile-menu-panel]") ||
      headerShell.querySelector(".mobile_menu_wrap");
    const mobileMenuWrap = menuPanel?.matches(".mobile_menu_wrap")
      ? menuPanel
      : menuPanel?.querySelector(".mobile_menu_wrap");

    if (!menuOpenButton || !menuPanel || !mobileMenuWrap) return null;

    const mobileSearchLink = mobileMenuWrap.querySelector(
      "[data-mobile-nav-search]",
    );
    const menuOverlay = headerShell.querySelector(".menu_overlay");
    const hamburgerIcon = menuOpenButton.querySelector(".icon_open");
    const closeIcon = menuOpenButton.querySelector(".icon_close");
    const desktopMedia = window.matchMedia(DESKTOP_QUERY);
    const hasPermanentHeaderRefresh = headerRoot.classList.contains(
      "gibbon-header-refresh__mobile",
    );
    const transitionBody = headerShell.closest(".transition-body");
    const cleanupCallbacks = [];
    const openLabel = menuOpenButton.dataset.openLabel || "Open menu";
    const closeLabel = menuOpenButton.dataset.closeLabel || "Close menu";
    const mobileFlowVariable = "--mobile-header-flow-height";
    const mobileMenuTopVariable = "--mobile-menu-top";
    let returnFocusElement = null;
    let mobileFlowOutsideHeight = 0;
    let ownsPageLock = false;
    let destroyed = false;

    function listen(target, eventName, handler, options) {
      if (!target?.addEventListener) return;

      target.addEventListener(eventName, handler, options);
      cleanupCallbacks.push(() =>
        target.removeEventListener(eventName, handler, options),
      );
    }

    function setIconVisibility(icon, isVisible) {
      if (!icon) return;

      icon.hidden = !isVisible;
      if (isVisible) {
        icon.style.removeProperty("display");
      } else {
        icon.style.display = "none";
      }
    }

    function setButtonState(isOpen) {
      menuOpenButton.setAttribute("aria-expanded", isOpen ? "true" : "false");
      menuOpenButton.setAttribute(
        "aria-label",
        isOpen ? closeLabel : openLabel,
      );
      setIconVisibility(hamburgerIcon, !isOpen);
      setIconVisibility(closeIcon, isOpen);
    }

    function setPanelState(isOpen) {
      menuPanel.setAttribute("aria-hidden", isOpen ? "false" : "true");

      if (isOpen) {
        menuPanel.removeAttribute("inert");
      } else {
        menuPanel.setAttribute("inert", "");
      }
    }

    function setPageLock(isLocked, forceUnlock) {
      if (isLocked) {
        document.documentElement.classList.add("overflow-hidden");
        document.body?.classList.add("overflow-hidden");
        ownsPageLock = true;
        return;
      }

      if (!ownsPageLock && !forceUnlock) return;

      document.documentElement.classList.remove("overflow-hidden");
      document.body?.classList.remove("overflow-hidden");
      ownsPageLock = false;
    }

    function setMobileNavFlowMetric() {
      if (!transitionBody) return 0;

      const shellRect = headerShell.getBoundingClientRect();
      const shellStyles = window.getComputedStyle(headerShell);
      const rootStyles = window.getComputedStyle(headerRoot);
      const shellMarginTop = Number.parseFloat(shellStyles.marginTop) || 0;
      const shellMarginBottom = Number.parseFloat(shellStyles.marginBottom) || 0;
      const rootMarginTop = Number.parseFloat(rootStyles.marginTop) || 0;
      const rootMarginBottom = Number.parseFloat(rootStyles.marginBottom) || 0;
      const flowMarginTop = Math.max(shellMarginTop, rootMarginTop);
      const flowMarginBottom = Math.max(shellMarginBottom, rootMarginBottom);
      const flowHeight = shellRect.height + flowMarginTop + flowMarginBottom;

      transitionBody.style.setProperty(
        mobileFlowVariable,
        `${flowHeight}px`,
      );

      return flowHeight;
    }

    function setMobileNavOpenMetrics(flowHeight) {
      if (!transitionBody) return;

      const shellRect = headerShell.getBoundingClientRect();
      mobileFlowOutsideHeight = Math.max(0, flowHeight - shellRect.height);
      transitionBody.style.setProperty(
        mobileMenuTopVariable,
        `${Math.max(0, shellRect.bottom)}px`,
      );
    }

    function refreshMobileNavOpenMetrics() {
      if (
        !transitionBody ||
        !headerShell.classList.contains("menu-open-header")
      ) {
        return;
      }

      const shellRect = headerShell.getBoundingClientRect();
      transitionBody.style.setProperty(
        mobileFlowVariable,
        `${shellRect.height + mobileFlowOutsideHeight}px`,
      );
      transitionBody.style.setProperty(
        mobileMenuTopVariable,
        `${Math.max(0, shellRect.bottom)}px`,
      );
    }

    function clearMobileNavLayoutMetrics() {
      if (!transitionBody) return;

      transitionBody.style.removeProperty(mobileFlowVariable);
      transitionBody.style.removeProperty(mobileMenuTopVariable);
      mobileFlowOutsideHeight = 0;
    }

    function setSubmenuState(submenu, isOpen) {
      if (!submenu) return;

      submenu.classList.toggle("show_submenu", isOpen);

      const listItem = submenu.parentElement;
      const submenuButton = directChild(listItem, ".sub_menu_open");
      const parentLink = Array.from(listItem?.children || []).find(
        (child) =>
          child.matches("a.no_link") &&
          !child.classList.contains("sub_menu_open"),
      );

      [submenuButton, parentLink].forEach((trigger) => {
        trigger?.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });

      const icon = submenuButton?.querySelector(".bi");
      if (icon) {
        icon.classList.toggle("bi-plus", !isOpen);
        icon.classList.toggle("bi-dash", isOpen);
      }
    }

    function closeSubmenuAndChildren(submenu) {
      if (!submenu) return;

      submenu.querySelectorAll("ul.show_submenu").forEach((childSubmenu) => {
        setSubmenuState(childSubmenu, false);
      });
      setSubmenuState(submenu, false);
    }

    function resetSubmenus() {
      mobileMenuWrap.querySelectorAll("ul.show_submenu").forEach((submenu) => {
        setSubmenuState(submenu, false);
      });

      mobileMenuWrap.querySelectorAll(".sub_menu_open").forEach((button) => {
        button.setAttribute("aria-expanded", "false");
        const icon = button.querySelector(".bi");
        if (icon) {
          icon.classList.remove("bi-dash");
          icon.classList.add("bi-plus");
        }
      });

      mobileMenuWrap
        .querySelectorAll("a.no_link[aria-expanded]")
        .forEach((link) => {
          link.setAttribute("aria-expanded", "false");
        });
    }

    function tabContentFor(tab, tabContents) {
      const href = tab.getAttribute("href");
      if (!href || href.charAt(0) !== "#") return null;

      const id = href.slice(1);
      return tabContents.find((content) => content.id === id) || null;
    }

    function resetTabs() {
      const tabs = Array.from(mobileMenuWrap.querySelectorAll(".nav-tabs a"));
      const tabContents = Array.from(
        mobileMenuWrap.querySelectorAll(".menu_contents_wrap > div"),
      );
      if (!tabs.length || !tabContents.length) return;

      tabs.forEach((tab) => tab.classList.remove("active"));
      tabContents.forEach((content) => {
        content.style.display = "none";
      });

      const firstTab =
        tabs.find((tab) => tab.getAttribute("href") === "#shop_wrap") ||
        tabs[0];
      const firstContent =
        tabContentFor(firstTab, tabContents) || tabContents[0];

      firstTab?.classList.add("active");
      if (firstContent) firstContent.style.display = "block";
    }

    function initialiseSubmenus() {
      mobileMenuWrap.querySelectorAll("ul li").forEach((listItem) => {
        const submenu = directSubmenu(listItem);
        if (!submenu) return;

        listItem.classList.add("hassub");

        let submenuButton = directChild(listItem, ".sub_menu_open");
        if (!submenuButton) {
          submenuButton = document.createElement("a");
          submenuButton.href = "#";
          submenuButton.className = "sub_menu_open";
          submenuButton.innerHTML =
            '<i class="bi bi-plus" aria-hidden="true"></i>';
          submenuButton.setAttribute("data-generated-submenu-control", "true");
          listItem.appendChild(submenuButton);
        }

        submenuButton.setAttribute("role", "button");
        if (!submenuButton.getAttribute("aria-label")) {
          submenuButton.setAttribute("aria-label", "Toggle submenu");
        }
        submenuButton.setAttribute(
          "aria-expanded",
          submenu.classList.contains("show_submenu") ? "true" : "false",
        );

        const parentLink = Array.from(listItem.children).find((child) => {
          if (!child.matches("a") || child.classList.contains("sub_menu_open"))
            return false;
          const href = child.getAttribute("href");
          return (
            href === "" ||
            href === "#" ||
            href === "javascript:;" ||
            href === "javascript:void(0);"
          );
        });

        if (parentLink) {
          parentLink.classList.add("no_link");
          parentLink.setAttribute(
            "aria-expanded",
            submenu.classList.contains("show_submenu") ? "true" : "false",
          );
        }
      });

      mobileMenuWrap.querySelectorAll("ul li ul").forEach((submenu) => {
        if (directChild(submenu, ".back_to_mainmenu")) return;

        const backItem = document.createElement("li");
        backItem.className = "back_to_mainmenu";
        backItem.setAttribute("data-generated-submenu-back", "true");
        backItem.innerHTML =
          '<a href="#"><i class="bi bi-chevron-left" aria-hidden="true"></i> Back</a>';
        submenu.appendChild(backItem);
      });
    }

    function openDefaultSubmenu() {
      if (!hasPermanentHeaderRefresh) return;

      let firstMenuItem = mobileMenuWrap.querySelector(
        ".mobile_menu ul.main_menu > li:first-child",
      );
      if (mobileMenuWrap.hasAttribute("data-mobile-open-item")) {
        const preferredTitle = (mobileMenuWrap.dataset.mobileOpenItem || "")
          .trim()
          .toLowerCase();
        if (!preferredTitle) return;
        firstMenuItem = Array.from(
          mobileMenuWrap.querySelectorAll("[data-mobile-menu-title]"),
        ).find((item) =>
          item.dataset.mobileMenuTitle.trim().toLowerCase() === preferredTitle,
        );
      }
      const firstSubmenu = directSubmenu(firstMenuItem);
      if (firstSubmenu) setSubmenuState(firstSubmenu, true);
    }

    function revealEditorBlock(blockId) {
      if (desktopMedia.matches || !blockId) return;
      const item = Array.from(
        mobileMenuWrap.querySelectorAll("[data-header-menu-block]"),
      ).find((candidate) => candidate.dataset.headerMenuBlock === String(blockId));
      if (!item) return;
      openMenu();
      resetSubmenus();
      const submenu = directSubmenu(item);
      if (submenu) setSubmenuState(submenu, true);
      item.scrollIntoView?.({ block: "nearest" });
    }

    function onHeaderBlockSelect(event) {
      if (!mobileMenuWrap.hasAttribute("data-header-block-menu")) return;
      const blockId = event.detail?.blockId;
      if (!blockId || !Array.from(
        mobileMenuWrap.querySelectorAll("[data-header-menu-block]"),
      ).some((item) => item.dataset.headerMenuBlock === String(blockId))) return;
      editorMobileBlockId = blockId;
      revealEditorBlock(blockId);
    }

    function onHeaderBlockDeselect(event) {
      if (editorMobileBlockId === event.detail?.blockId) editorMobileBlockId = null;
    }

    function normaliseScrollerLabels() {
      if (!hasPermanentHeaderRefresh) return;

      const labelsByPath = {
        "/collections/accessories": "Accessories",
        "/products/gibbon-slackyard": "SlackYard",
        "/collections/slacklines": "SlackLines",
        "/collections/slackracks": "SlackRacks",
      };

      mobileMenuWrap
        .querySelectorAll(".menu_collection_scroller a[href]")
        .forEach((link) => {
          const path = (link.getAttribute("href") || "")
            .split("?")[0]
            .replace(/\/$/, "");
          const label = labelsByPath[path];
          const labelElement = link.querySelector("span");

          if (label && labelElement) labelElement.textContent = label;
        });
    }

    function openMenu() {
      if (destroyed || desktopMedia.matches) return;

      returnFocusElement =
        document.activeElement &&
        typeof document.activeElement.focus === "function"
          ? document.activeElement
          : menuOpenButton;

      const mobileNavFlowHeight = setMobileNavFlowMetric();
      headerShell.classList.add("menu-open-header");
      setMobileNavOpenMetrics(mobileNavFlowHeight);
      openDefaultSubmenu();
      mobileMenuWrap.classList.add("menu_show");
      setPanelState(true);
      if (menuOverlay) menuOverlay.style.display = "block";
      setPageLock(true);
      setButtonState(true);
    }

    function closeMenu(options) {
      const settings = options || {};
      const wasOpen = mobileMenuWrap.classList.contains("menu_show");
      const headerWasOpen = headerShell.classList.contains("menu-open-header");

      mobileMenuWrap.classList.remove("menu_show");
      setPanelState(false);
      if (menuOverlay) menuOverlay.style.display = "none";
      headerShell.classList.remove("menu-open-header");
      setPageLock(false, wasOpen || headerWasOpen);
      clearMobileNavLayoutMetrics();
      setButtonState(false);
      resetSubmenus();

      if (settings.resetTabs) resetTabs();

      if (
        wasOpen &&
        settings.restoreFocus &&
        returnFocusElement?.isConnected &&
        typeof returnFocusElement.focus === "function"
      ) {
        returnFocusElement.focus();
      }

      returnFocusElement = null;
    }

    function toggleMenu() {
      if (mobileMenuWrap.classList.contains("menu_show")) {
        closeMenu({ restoreFocus: true });
      } else {
        openMenu();
      }
    }

    function onMenuClick(event) {
      const clickedElement =
        event.target instanceof Element ? event.target : null;
      if (!clickedElement) return;

      const tab = clickedElement.closest(".nav-tabs a");
      if (tab && mobileMenuWrap.contains(tab)) {
        const tabs = Array.from(mobileMenuWrap.querySelectorAll(".nav-tabs a"));
        const tabContents = Array.from(
          mobileMenuWrap.querySelectorAll(".menu_contents_wrap > div"),
        );
        const content = tabContentFor(tab, tabContents);
        if (!content) return;

        event.preventDefault();
        tabs.forEach((item) => item.classList.remove("active"));
        tabContents.forEach((item) => {
          item.style.display = "none";
        });
        tab.classList.add("active");
        content.style.display = "block";
        return;
      }

      const backButton = clickedElement.closest(".back_to_mainmenu");
      if (backButton && mobileMenuWrap.contains(backButton)) {
        event.preventDefault();
        closeSubmenuAndChildren(backButton.parentElement);
        return;
      }

      const submenuButton = clickedElement.closest(".sub_menu_open");
      if (submenuButton && mobileMenuWrap.contains(submenuButton)) {
        event.preventDefault();
        const submenu = directSubmenu(submenuButton.parentElement);
        if (submenu) {
          setSubmenuState(submenu, !submenu.classList.contains("show_submenu"));
        }
        return;
      }

      const parentLink = clickedElement.closest("a.no_link");
      if (parentLink && mobileMenuWrap.contains(parentLink)) {
        const submenu = directSubmenu(parentLink.parentElement);
        if (!submenu) return;

        event.preventDefault();
        if (submenu.classList.contains("show_submenu")) {
          closeSubmenuAndChildren(submenu);
        } else {
          setSubmenuState(submenu, true);
        }
      }
    }

    function onKeydown(event) {
      if (
        event.key !== "Escape" ||
        !mobileMenuWrap.classList.contains("menu_show")
      )
        return;

      event.preventDefault();
      closeMenu({ restoreFocus: true });
    }

    function onDesktopChange(event) {
      if (event.matches) {
        closeMenu({ restoreFocus: false, resetTabs: true });
      }
    }

    normaliseScrollerLabels();
    initialiseSubmenus();
    resetTabs();
    setButtonState(false);
    setPanelState(false);

    listen(menuOpenButton, "click", toggleMenu);
    if (mobileSearchLink) {
      listen(mobileSearchLink, "click", () => {
        closeMenu({ restoreFocus: false });
      });
    }
    listen(mobileMenuWrap, "click", onMenuClick);
    listen(document, "keydown", onKeydown);
    listen(document, "shopify:block:select", onHeaderBlockSelect);
    listen(document, "shopify:block:deselect", onHeaderBlockDeselect);
    listen(window, "resize", refreshMobileNavOpenMetrics);

    headerShell
      .querySelectorAll(".menu_close, .menu_overlay")
      .forEach((closeControl) => {
        listen(closeControl, "click", () => closeMenu({ restoreFocus: true }));
      });

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener("change", onDesktopChange);
      cleanupCallbacks.push(() =>
        desktopMedia.removeEventListener("change", onDesktopChange),
      );
    } else if (desktopMedia.addListener) {
      desktopMedia.addListener(onDesktopChange);
      cleanupCallbacks.push(() => desktopMedia.removeListener(onDesktopChange));
    }

    if (
      desktopMedia.matches ||
      mobileMenuWrap.classList.contains("menu_show")
    ) {
      closeMenu({ restoreFocus: false, resetTabs: desktopMedia.matches });
    }
    if (editorMobileBlockId) revealEditorBlock(editorMobileBlockId);

    return {
      root: headerRoot,
      shell: headerShell,
      destroy() {
        if (destroyed) return;

        closeMenu({ restoreFocus: false, resetTabs: true });
        cleanupCallbacks.splice(0).forEach((cleanup) => cleanup());
        destroyed = true;
      },
    };
  }

  let activeController = null;
  const managerCleanupCallbacks = [];

  function mount(scope) {
    const headerRoot = findHeaderRoot(scope);
    if (!headerRoot) return false;

    activeController?.destroy();
    activeController = createController(headerRoot);
    return Boolean(activeController);
  }

  function onSectionLoad(event) {
    if (findHeaderRoot(event.target)) {
      mount(event.target);
    }
  }

  function onSectionUnload(event) {
    if (activeController && event.target.contains(activeController.root)) {
      activeController.destroy();
      activeController = null;
    }
  }

  function boot() {
    mount(document);
  }

  document.addEventListener("shopify:section:load", onSectionLoad);
  managerCleanupCallbacks.push(() =>
    document.removeEventListener("shopify:section:load", onSectionLoad),
  );

  document.addEventListener("shopify:section:unload", onSectionUnload);
  managerCleanupCallbacks.push(() =>
    document.removeEventListener("shopify:section:unload", onSectionUnload),
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
    managerCleanupCallbacks.push(() =>
      document.removeEventListener("DOMContentLoaded", boot),
    );
  } else {
    boot();
  }

  window[CONTROLLER_KEY] = {
    destroy() {
      activeController?.destroy();
      activeController = null;
      managerCleanupCallbacks.splice(0).forEach((cleanup) => cleanup());
    },
  };
})();
